<template>
  <div class="student-statement">
    <PageSpinner :onloader="loading ? 'flex' : 'none'" />
    <base-header type="gradient-info" class="pb-6 pb-8 pt-5 pt-md-8">
      <router-link to="/dashboard" class="text-white">Dashboard</router-link>
      <router-link to="/school/reports-and-analytics" class="text-white"
        ><i class="fa fa-ellipsis-v mx-2"></i>Reports and Analytics</router-link
      >
      <span class="text-white"
        ><i class="fa fa-ellipsis-v mx-2"></i>Student Account Statement</span
      >
    </base-header>
    <main class="container-fluid mt--7 statement-shell">
      <header class="page-heading">
        <div>
          <span class="eyebrow">FINANCE &amp; ACCOUNTABILITY</span>
          <h1>Student account statement</h1>
          <p>
            Review one student's billed fees, recorded payments, and reported
            balance.
          </p>
        </div>
        <div class="heading-tools">
          <span class="mode-pill" :class="{ sample: mockMode }"
            ><i class="fa" :class="mockMode ? 'fa-flask' : 'fa-database'"></i
            >{{ mockMode ? "Sample preview" : "Live school records" }}</span
          ><button
            class="btn btn-sm btn-outline-primary"
            type="button"
            :disabled="loading"
            @click="toggleMode"
          >
            {{ mockMode ? "Return to live data" : "Preview sample data" }}
          </button>
        </div>
      </header>
      <section class="filter-panel card border-0 shadow-sm mb-4">
        <div class="card-body">
          <div class="section-heading">
            <h2>Statement filters</h2>
            <p>
              Choose a registered term and one student. Group filters help
              narrow the student list.
            </p>
          </div>
          <div class="filter-grid">
            <div class="form-group">
              <label for="registeredTerm"
                >Registered term
                <span v-if="listLoading.terms" class="inline-loader"
                  ><i></i>Loading</span
                ></label
              ><select
                id="registeredTerm"
                v-model="filters.RegisteredTermId"
                class="form-control"
                :disabled="listLoading.terms"
                @change="onRegisteredTermChange"
              >
                <option value="">
                  {{
                    listLoading.terms
                      ? "Loading terms…"
                      : "Select registered term"
                  }}
                </option>
                <option
                  v-for="item in registeredTerms"
                  :key="item.registeredTermId"
                  :value="String(item.registeredTermId)"
                >
                  {{ item.schSession }} · {{ item.schTerm }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="class"
                >Class
                <span v-if="listLoading.classes" class="inline-loader"
                  ><i></i>Loading</span
                ></label
              ><select
                id="class"
                v-model="filters.ClassId"
                class="form-control"
                :disabled="!filters.RegisteredTermId || listLoading.classes"
                @change="onClassChange"
              >
                <option value="">
                  {{
                    listLoading.classes ? "Loading classes…" : "Select class"
                  }}
                </option>
                <option
                  v-for="item in classes"
                  :key="item.Id"
                  :value="String(item.Id)"
                >
                  {{ item.Sch_Class || item.ClassName || item.ClassOption }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="option">Option</label
              ><select
                id="option"
                v-model="filters.Option"
                class="form-control"
                :disabled="!filters.ClassId"
                @change="clearStudent"
              >
                <option value="">All options</option>
                <option v-for="item in optionOptions" :key="item" :value="item">
                  {{ item }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="section">Section / arm</label
              ><select
                id="section"
                v-model="filters.Section"
                class="form-control"
                :disabled="!filters.ClassId"
                @change="clearStudent"
              >
                <option value="">All sections</option>
                <option
                  v-for="item in sectionOptions"
                  :key="item"
                  :value="item"
                >
                  {{ item }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="student"
                >Student
                <span v-if="listLoading.students" class="inline-loader"
                  ><i></i>Loading</span
                ></label
              ><select
                id="student"
                v-model="filters.StudentId"
                class="form-control"
                :disabled="!filters.ClassId || listLoading.students"
                @change="clearReport"
              >
                <option value="">
                  {{
                    listLoading.students
                      ? "Loading students…"
                      : "Select student"
                  }}
                </option>
                <option
                  v-for="item in filteredStudents"
                  :key="item.Id"
                  :value="String(item.Id)"
                >
                  {{ studentLabel(item) }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="fromDate">Payment date from</label
              ><input
                id="fromDate"
                v-model="filters.StartDate"
                type="date"
                class="form-control"
                :max="filters.EndDate || undefined"
              />
            </div>
            <div class="form-group">
              <label for="toDate">Payment date to</label
              ><input
                id="toDate"
                v-model="filters.EndDate"
                type="date"
                class="form-control"
                :min="filters.StartDate || undefined"
              />
            </div>
            <div class="filter-actions">
              <button
                class="btn btn-primary"
                type="button"
                :disabled="loading || !canApply"
                @click="applyFilters"
              >
                <i class="fa fa-filter mr-1"></i>View statement</button
              ><button
                class="btn btn-outline-secondary"
                type="button"
                :disabled="loading"
                @click="resetFilters"
              >
                Reset filters
              </button>
            </div>
          </div>
          <p class="filter-note">
            <i class="fa fa-circle-info mr-1"></i>Each statement is requested
            for one selected student, session, and term. Payment dates filter
            the displayed payment records.
          </p>
        </div>
      </section>
      <div v-if="errorMessage" class="alert alert-danger" role="alert">
        {{ errorMessage }}
      </div>
      <div v-if="mockMode && hasGenerated" class="sample-banner">
        <i class="fa fa-flask"></i
        ><span
          ><strong>Sample preview only.</strong> These illustrative records are
          not official school financial data.</span
        >
      </div>
      <section
        v-if="hasGenerated && !loading"
        id="printable-statement"
        class="statement-content"
      >
        <header class="statement-header">
          <div>
            <span class="eyebrow">STUDENT ACCOUNT</span>
            <h2>Account statement</h2>
            <p>
              {{ selectedSessionName
              }}<span v-if="selectedTermName"> · {{ selectedTermName }}</span> ·
              Generated {{ generatedAt }}
            </p>
          </div>
          <div class="student-identity">
            <strong>{{ selectedStudentName }}</strong
            ><span>{{
              admissionNumber || "Admission number not provided"
            }}</span
            ><span
              >{{ selectedClassName
              }}<template v-if="selectedOption">
                · {{ selectedOption }}</template
              ><template v-if="selectedSection">
                · {{ selectedSection }}</template
              ></span
            >
          </div>
        </header>
        <div class="summary-grid">
          <article class="summary-card">
            <small>Fees assessed</small
            ><strong>{{ money(studentAccount.TotalBillAmount) }}</strong
            ><span>Term bill total returned by SchoolDock</span>
          </article>
          <article class="summary-card positive">
            <small>Payments recorded</small
            ><strong>{{ money(studentAccount.TotalAmount) }}</strong
            ><span
              >{{ paymentRows.length }} payment record{{
                paymentRows.length === 1 ? "" : "s"
              }}
              returned</span
            >
          </article>
          <article
            class="summary-card"
            :class="balanceValue > 0 ? 'attention' : 'settled'"
          >
            <small>{{
              hasCreditBalance ? "Credit balance" : "Balance reported"
            }}</small
            ><strong
              >{{ money(Math.abs(balanceValue))
              }}{{ hasCreditBalance ? " credit" : "" }}</strong
            ><span>{{
              balanceSettled
                ? "No outstanding amount reported"
                : "Outstanding amount reported by SchoolDock"
            }}</span>
          </article>
          <article class="summary-card">
            <small>Statement period</small
            ><strong class="period-value">{{ selectedTermName }}</strong
            ><span
              >{{ filters.StartDate || "All payment dates"
              }}{{ filters.EndDate ? ` to ${filters.EndDate}` : "" }}</span
            >
          </article>
        </div>
        <div class="notice">
          <i class="fa fa-circle-info"></i
          ><span
            >Live service data supplies term bill, total paid, balance, and
            payment details. Invoice lines, due dates, payment statuses,
            discounts, scholarships, refunds, and opening balances are not
            exposed by this integration, so those values are not estimated
            here.</span
          >
        </div>
        <section class="chart-grid">
          <article class="chart-card card border-0 shadow-sm">
            <div class="card-body">
              <div class="section-heading">
                <h3>Recorded payments by month</h3>
                <p>Payment detail amounts grouped by payment date.</p>
              </div>
              <div v-if="monthlyPayments.length" class="bar-chart">
                <div
                  v-for="month in monthlyPayments"
                  :key="month.key"
                  class="bar-column"
                >
                  <strong>{{ compactMoney(month.amount) }}</strong
                  ><i :style="{ height: `${month.height}%` }"></i
                  ><small>{{ month.label }}</small>
                </div>
              </div>
              <div v-else class="chart-empty">
                No dated payments are available in this period.
              </div>
            </div>
          </article>
          <article class="chart-card card border-0 shadow-sm">
            <div class="card-body">
              <div class="section-heading">
                <h3>Fees and payments</h3>
                <p>Reported totals returned for the selected term.</p>
              </div>
              <div class="compare-chart">
                <div
                  v-for="metric in comparisonMetrics"
                  :key="metric.label"
                  class="compare-row"
                >
                  <span>{{ metric.label }}</span>
                  <div>
                    <i
                      :class="metric.tone"
                      :style="{ width: `${metric.width}%` }"
                    ></i>
                  </div>
                  <strong>{{ money(metric.value) }}</strong>
                </div>
              </div>
            </div>
          </article>
        </section>
        <section class="table-card card border-0 shadow-sm">
          <div class="table-heading">
            <div>
              <h3>Payment history</h3>
              <p>
                {{ filteredRows.length }} payment records shown · balance
                remains the API-reported value
              </p>
            </div>
            <div class="table-tools">
              <label class="search-box"
                ><i class="fa fa-search"></i
                ><input
                  v-model.trim="search"
                  type="search"
                  placeholder="Search payments"
                  aria-label="Search payments" /></label
              ><button
                class="btn btn-outline-primary btn-sm"
                type="button"
                :disabled="!filteredRows.length"
                @click="exportExcel"
              >
                <i class="fa fa-file-excel mr-1"></i>Excel</button
              ><button
                class="btn btn-outline-secondary btn-sm"
                type="button"
                :disabled="!filteredRows.length"
                @click="exportCsv"
              >
                CSV</button
              ><button
                class="btn btn-outline-secondary btn-sm"
                type="button"
                :disabled="!hasGenerated"
                @click="printStatement"
              >
                <i class="fa fa-print mr-1"></i>Print statement
              </button>
            </div>
          </div>
          <div v-if="filteredRows.length" class="table-responsive">
            <table class="table statement-table mb-0">
              <thead>
                <tr>
                  <th @click="sortBy('date')">
                    Payment date <i class="fa fa-sort ml-1"></i>
                  </th>
                  <th @click="sortBy('reference')">
                    Reference <i class="fa fa-sort ml-1"></i>
                  </th>
                  <th>Description</th>
                  <th>Method</th>
                  <th class="text-right" @click="sortBy('amount')">
                    Amount received <i class="fa fa-sort ml-1"></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in pagedRows" :key="row._key">
                  <td>{{ formatDate(row.date) }}</td>
                  <td>{{ row.reference }}</td>
                  <td>{{ row.description }}</td>
                  <td>{{ row.method }}</td>
                  <td class="text-right amount">{{ money(row.amount) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th colspan="4">Payments shown</th>
                  <th class="text-right">{{ money(filteredTotal) }}</th>
                </tr>
              </tfoot>
            </table>
          </div>
          <div v-else class="empty-state">
            <i class="fa fa-receipt"></i>
            <h3>No payment records found</h3>
            <p>
              {{
                search || filters.StartDate || filters.EndDate
                  ? "Adjust the search or date range and try again."
                  : "No payment details were returned for this student and term."
              }}
            </p>
          </div>
          <div v-if="filteredRows.length" class="pagination-row">
            <span
              >Showing {{ pageStart + 1 }}–{{
                Math.min(pageStart + pageSize, filteredRows.length)
              }}
              of {{ filteredRows.length }}</span
            >
            <div>
              <select v-model.number="pageSize" aria-label="Rows per page">
                <option :value="10">10 rows</option>
                <option :value="20">20 rows</option>
                <option :value="50">50 rows</option></select
              ><button
                class="btn btn-light btn-sm"
                :disabled="page <= 1"
                @click="page--"
              >
                Previous</button
              ><span>{{ page }} / {{ pageCount }}</span
              ><button
                class="btn btn-light btn-sm"
                :disabled="page >= pageCount"
                @click="page++"
              >
                Next
              </button>
            </div>
          </div>
        </section>
        <footer class="statement-footer">
          This statement reflects records available in SchoolDock at the time it
          was generated. Please contact the school finance office to reconcile
          discrepancies.
        </footer>
      </section>
      <div
        v-else-if="!hasGenerated && !loading"
        class="welcome-state card border-0 shadow-sm"
      >
        <i class="fa fa-file-invoice-dollar"></i>
        <h2>Generate a student statement</h2>
        <p>
          Select a registered term, class, and student to view that student's
          account activity.
        </p>
      </div>
    </main>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import PageSpinner from "@/components/PageSpinner";

export default {
  name: "StudentAccountStatementReport",
  components: { PageSpinner },
  data() {
    return {
      loading: false,
      listLoading: {
        terms: false,
        classes: false,
        students: false,
      },
      errorMessage: "",
      hasGenerated: false,
      mockMode: false,
      registeredTerms: [],
      classes: [],
      students: [],
      filters: {
        RegisteredTermId: "",
        ClassId: "",
        StudentId: "",
        Option: "",
        Section: "",
        StartDate: "",
        EndDate: "",
      },
      studentAccount: {},
      paymentRows: [],
      search: "",
      sortKey: "date",
      sortDirection: "desc",
      page: 1,
      pageSize: 20,
      requestId: 0,
      listRequestIds: { terms: 0, classes: 0, students: 0 },
      sampleData: {
        terms: [
          {
            registeredTermId: "sample-term",
            schSession: "2025/2026",
            schTerm: "First Term",
            sessionId: "sample-session",
            termId: "sample-term-id",
          },
          {
            registeredTermId: "sample-term-2",
            schSession: "2025/2026",
            schTerm: "Second Term",
            sessionId: "sample-session",
            termId: "sample-term-id-2",
          },
        ],
        classes: [{ Id: "sample-class", Sch_Class: "Primary 5" }],
        students: [
          {
            Id: "sample-student",
            StudentName: "Amina Okafor",
            AdmissionNo: "SD-2022-041",
            ClassOption: "General",
            SectionName: "Blue",
          },
          {
            Id: "sample-student-2",
            StudentName: "Daniel Bello",
            AdmissionNo: "SD-2022-052",
            ClassOption: "General",
            SectionName: "Gold",
          },
        ],
        accounts: {
          "sample-student": {
            TotalBillAmount: 385000,
            TotalAmount: 250000,
            AmountBalance: 135000,
            AmountPaidDetails: [
              {
                Id: "PAY-101",
                PaymentDate: "2025-09-15",
                AmountPaid: 150000,
                ReceiptNo: "RCT-2025-101",
                PaymentMethod: "Bank transfer",
                Narration: "First installment",
              },
              {
                Id: "PAY-118",
                PaymentDate: "2025-10-20",
                AmountPaid: 100000,
                ReceiptNo: "RCT-2025-118",
                PaymentMethod: "Card",
                Narration: "Second installment",
              },
            ],
          },
          "sample-student-2": {
            TotalBillAmount: 385000,
            TotalAmount: 385000,
            AmountBalance: 0,
            AmountPaidDetails: [
              {
                Id: "PAY-204",
                PaymentDate: "2025-09-18",
                AmountPaid: 200000,
                ReceiptNo: "RCT-2025-204",
                PaymentMethod: "Bank transfer",
                Narration: "First installment",
              },
              {
                Id: "PAY-229",
                PaymentDate: "2025-11-02",
                AmountPaid: 185000,
                ReceiptNo: "RCT-2025-229",
                PaymentMethod: "Cash",
                Narration: "Final installment",
              },
            ],
          },
        },
      },
    };
  },
  computed: {
    canApply() {
      return Boolean(
        this.filters.RegisteredTermId &&
          this.filters.ClassId &&
          this.filters.StudentId &&
          (!this.filters.StartDate ||
            !this.filters.EndDate ||
            this.filters.StartDate <= this.filters.EndDate)
      );
    },
    selectedRegisteredTerm() {
      return (
        this.registeredTerms.find(
          (item) =>
            String(item.registeredTermId) === this.filters.RegisteredTermId
        ) || {}
      );
    },
    selectedSessionName() {
      return this.selectedRegisteredTerm.schSession || "";
    },
    selectedSessionId() {
      return (
        this.selectedRegisteredTerm.sessionId ||
        this.selectedRegisteredTerm.SessionId ||
        this.selectedRegisteredTerm.sessionID ||
        ""
      );
    },
    selectedTermId() {
      return (
        this.selectedRegisteredTerm.termId ||
        this.selectedRegisteredTerm.TermId ||
        this.selectedRegisteredTerm.termID ||
        ""
      );
    },
    selectedTermName() {
      return this.selectedRegisteredTerm.schTerm || "";
    },
    selectedStudent() {
      return (
        this.students.find(
          (item) => String(item.Id) === this.filters.StudentId
        ) || {}
      );
    },
    selectedStudentName() {
      return (
        this.selectedStudent.StudentName ||
        this.selectedStudent.FullName ||
        "Selected student"
      );
    },
    admissionNumber() {
      return (
        this.selectedStudent.AdmissionNo ||
        this.selectedStudent.AdmissionNumber ||
        this.selectedStudent.RegNo ||
        ""
      );
    },
    selectedClassName() {
      const row = this.classes.find(
        (item) => String(item.Id) === this.filters.ClassId
      );
      return row ? row.Sch_Class || row.ClassName || row.ClassOption : "";
    },
    selectedOption() {
      return (
        this.filters.Option ||
        this.selectedStudent.ClassOption ||
        this.selectedStudent.OptionName ||
        ""
      );
    },
    selectedSection() {
      return (
        this.filters.Section ||
        this.selectedStudent.SectionName ||
        this.selectedStudent.ArmName ||
        ""
      );
    },
    optionOptions() {
      return [
        ...new Set(
          this.students
            .map((item) => item.ClassOption || item.OptionName)
            .filter(Boolean)
        ),
      ].sort();
    },
    sectionOptions() {
      return [
        ...new Set(
          this.students
            .filter(
              (item) =>
                !this.filters.Option ||
                (item.ClassOption || item.OptionName) === this.filters.Option
            )
            .map((item) => item.SectionName || item.ArmName)
            .filter(Boolean)
        ),
      ].sort();
    },
    filteredStudents() {
      return this.students.filter(
        (item) =>
          (!this.filters.Option ||
            (item.ClassOption || item.OptionName) === this.filters.Option) &&
          (!this.filters.Section ||
            (item.SectionName || item.ArmName) === this.filters.Section)
      );
    },
    balanceValue() {
      return this.numberValue(this.studentAccount.AmountBalance);
    },
    hasCreditBalance() {
      return this.balanceValue < 0;
    },
    balanceSettled() {
      return this.balanceValue <= 0;
    },
    filteredRows() {
      const q = this.search.toLowerCase();
      return this.paymentRows
        .filter(
          (row) =>
            (!this.filters.StartDate ||
              (row.date && row.date >= this.filters.StartDate)) &&
            (!this.filters.EndDate ||
              (row.date && row.date <= this.filters.EndDate)) &&
            (!q ||
              [row.reference, row.description, row.method]
                .join(" ")
                .toLowerCase()
                .includes(q))
        )
        .sort((a, b) => {
          const av = a[this.sortKey] || "";
          const bv = b[this.sortKey] || "";
          const diff =
            typeof av === "number" && typeof bv === "number"
              ? av - bv
              : String(av).localeCompare(String(bv));
          return this.sortDirection === "asc" ? diff : -diff;
        });
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    },
    pageStart() {
      return (this.page - 1) * this.pageSize;
    },
    pagedRows() {
      return this.filteredRows.slice(
        this.pageStart,
        this.pageStart + this.pageSize
      );
    },
    filteredTotal() {
      return this.filteredRows.reduce((sum, row) => sum + row.amount, 0);
    },
    monthlyPayments() {
      const groups = {};
      this.filteredRows.forEach((row) => {
        if (row.date) {
          const key = row.date.slice(0, 7);
          groups[key] = (groups[key] || 0) + row.amount;
        }
      });
      const entries = Object.keys(groups)
        .sort()
        .map((key) => ({
          key,
          label: new Intl.DateTimeFormat("en", { month: "short" }).format(
            new Date(`${key}-01T00:00:00`)
          ),
          amount: groups[key],
        }));
      const max = Math.max(1, ...entries.map((item) => item.amount));
      return entries.map((item) => ({
        ...item,
        height: Math.max(5, (item.amount / max) * 100),
      }));
    },
    comparisonMetrics() {
      const values = [
        {
          label: "Fees assessed",
          value: this.numberValue(this.studentAccount.TotalBillAmount),
          tone: "blue",
        },
        {
          label: "Payments recorded",
          value: this.numberValue(this.studentAccount.TotalAmount),
          tone: "green",
        },
        {
          label: "Reported balance",
          value: Math.abs(this.balanceValue),
          tone: "amber",
        },
      ];
      const max = Math.max(1, ...values.map((item) => item.value));
      return values.map((item) => ({
        ...item,
        width: item.value ? Math.max(3, (item.value / max) * 100) : 0,
      }));
    },
    generatedAt() {
      return new Intl.DateTimeFormat("en-NG", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date());
    },
  },
  watch: {
    pageSize() {
      this.page = 1;
    },
  },
  created() {
    this.loadRegisteredTerms();
  },
  methods: {
    normalizeList(result) {
      return Array.isArray(result)
        ? result
        : result && Array.isArray(result.data)
        ? result.data
        : [];
    },
    studentLabel(item) {
      const name = item.StudentName || item.FullName || "Student";
      const admission = item.AdmissionNo || item.AdmissionNumber || item.RegNo;
      return admission ? `${name} · ${admission}` : name;
    },
    numberValue(value) {
      const number =
        typeof value === "string"
          ? Number(value.replace(/[₦,\s]/g, ""))
          : Number(value);
      return Number.isFinite(number) ? number : 0;
    },
    money(value) {
      return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        minimumFractionDigits: 2,
      }).format(this.numberValue(value));
    },
    compactMoney(value) {
      const number = this.numberValue(value);
      return number >= 1000000
        ? `₦${(number / 1000000).toFixed(1)}m`
        : number >= 1000
        ? `₦${(number / 1000).toFixed(0)}k`
        : `₦${number.toFixed(0)}`;
    },
    dateValue(value) {
      if (!value) return "";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? String(value).slice(0, 10)
        : date.toISOString().slice(0, 10);
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(`${value}T00:00:00`);
      return Number.isNaN(date.getTime())
        ? value
        : new Intl.DateTimeFormat("en-NG", { dateStyle: "medium" }).format(
            date
          );
    },
    async loadRegisteredTerms() {
      this.listLoading.terms = true;
      try {
        this.registeredTerms = this.mockMode
          ? this.sampleData.terms
          : this.normalizeList(
              await this.$store.dispatch(
                "PortalSchoolSessionTerm/GetPortalRegisteredTerm",
                { SearchValue: null, PageNumber: 1, PageSize: 100 }
              )
            );
      } catch (error) {
        this.errorMessage = this.errorText(
          error,
          "Could not load registered terms."
        );
      } finally {
        this.listLoading.terms = false;
      }
    },
    async onRegisteredTermChange() {
      const requestId = ++this.listRequestIds.classes;
      this.listRequestIds.students += 1;
      this.listLoading.students = false;
      this.filters.ClassId = "";
      this.filters.StudentId = "";
      this.classes = [];
      this.students = [];
      this.clearReport();
      if (!this.filters.RegisteredTermId) return;
      if (!this.selectedSessionId || !this.selectedTermId) {
        this.errorMessage =
          "The selected registered term does not include the session and term IDs required by the billing service.";
        return;
      }
      this.listLoading.classes = true;
      try {
        const result = this.mockMode
          ? this.sampleData.classes
          : await this.$store.dispatch("GetClassInSchoolBill", {
              SessionId: this.selectedSessionId,
              TermId: this.selectedTermId,
            });
        if (requestId === this.listRequestIds.classes)
          this.classes = this.mockMode ? result : this.normalizeList(result);
      } catch (error) {
        if (requestId === this.listRequestIds.classes)
          this.errorMessage = this.errorText(error, "Could not load classes.");
      } finally {
        if (requestId === this.listRequestIds.classes)
          this.listLoading.classes = false;
      }
    },
    async onClassChange() {
      const requestId = ++this.listRequestIds.students;
      this.filters.StudentId = "";
      this.filters.Option = "";
      this.filters.Section = "";
      this.students = [];
      this.clearReport();
      if (!this.filters.ClassId) return;
      this.listLoading.students = true;
      try {
        const result = this.mockMode
          ? this.sampleData.students
          : await this.$store.dispatch(
              "getByClassIdStudent",
              this.filters.ClassId
            );
        if (requestId === this.listRequestIds.students)
          this.students = this.mockMode ? result : this.normalizeList(result);
      } catch (error) {
        if (requestId === this.listRequestIds.students)
          this.errorMessage = this.errorText(
            error,
            "Could not load students for this class."
          );
      } finally {
        if (requestId === this.listRequestIds.students)
          this.listLoading.students = false;
      }
    },
    clearStudent() {
      this.filters.StudentId = "";
      this.clearReport();
    },
    clearReport() {
      this.requestId += 1;
      this.hasGenerated = false;
      this.studentAccount = {};
      this.paymentRows = [];
      this.errorMessage = "";
    },
    async applyFilters() {
      if (!this.canApply) return;
      const id = ++this.requestId;
      this.loading = true;
      this.hasGenerated = false;
      this.errorMessage = "";
      this.studentAccount = {};
      this.paymentRows = [];
      try {
        const result = this.mockMode
          ? this.sampleData.accounts[this.filters.StudentId]
          : await this.$store.dispatch("getSchoolBillPayment", {
              StudentId: this.filters.StudentId,
              SessionId: this.selectedSessionId,
              TermId: this.selectedTermId,
            });
        if (id !== this.requestId) return;
        this.studentAccount = result || {};
        this.paymentRows = this.normalizeList(
          this.studentAccount.AmountPaidDetails
        ).map((item, index) => ({
          _key: item.Id || `payment-${index}`,
          date: this.dateValue(item.PaymentDate),
          reference: item.ReceiptNo || item.ReferenceNo || item.Id || "—",
          method: item.PaymentMethod || item.PaymentMode || item.Method || "—",
          description:
            item.Naration ||
            item.Narration ||
            item.Description ||
            "School fee payment",
          amount: this.numberValue(
            item.AmountPaid != null ? item.AmountPaid : item.Amount
          ),
        }));
        this.search = "";
        this.page = 1;
        this.hasGenerated = true;
      } catch (error) {
        if (id === this.requestId)
          this.errorMessage = this.errorText(
            error,
            "Could not load this student's account statement."
          );
      } finally {
        if (id === this.requestId) this.loading = false;
      }
    },
    errorText(error, fallback) {
      return error && error.response && error.response.data
        ? String(error.response.data)
        : error && error.message
        ? error.message
        : fallback;
    },
    sortBy(key) {
      if (this.sortKey === key)
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      else {
        this.sortKey = key;
        this.sortDirection = key === "date" ? "desc" : "asc";
      }
      this.page = 1;
    },
    exportRows() {
      return this.filteredRows.map((row) => ({
        "Payment date": row.date,
        Reference: row.reference,
        Description: row.description,
        Method: row.method,
        "Amount received": row.amount,
      }));
    },
    exportExcel() {
      const wb = XLSX.utils.book_new();
      const summary = XLSX.utils.aoa_to_sheet([
        ["Student account statement"],
        [
          "Data mode",
          this.mockMode ? "Sample preview only" : "Live school data",
        ],
        ["Student", this.selectedStudentName],
        ["Admission number", this.admissionNumber],
        ["Class", this.selectedClassName],
        [
          "Registered term",
          `${this.selectedSessionName} · ${this.selectedTermName}`,
        ],
        [
          "Fees assessed",
          this.numberValue(this.studentAccount.TotalBillAmount),
        ],
        [
          "Payments recorded",
          this.numberValue(this.studentAccount.TotalAmount),
        ],
        ["Balance reported", this.balanceValue],
        ["Generated", this.generatedAt],
      ]);
      XLSX.utils.book_append_sheet(wb, summary, "Statement");
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(this.exportRows()),
        "Payments"
      );
      XLSX.writeFile(
        wb,
        `student-account-${this.filters.StudentId}-${this.filters.RegisteredTermId}.xlsx`
      );
    },
    exportCsv() {
      const rows = this.exportRows();
      const quote = (value) =>
        `"${String(value == null ? "" : value).replace(/"/g, '""')}"`;
      const csv = [
        Object.keys(
          rows[0] || {
            "Payment date": "",
            Reference: "",
            Description: "",
            Method: "",
            "Amount received": "",
          }
        )
          .map(quote)
          .join(","),
        ...rows.map((row) => Object.values(row).map(quote).join(",")),
      ].join("\r\n");
      const link = document.createElement("a");
      link.href = URL.createObjectURL(
        new Blob([csv], { type: "text/csv;charset=utf-8" })
      );
      link.download = `student-account-${this.filters.StudentId}-${this.filters.RegisteredTermId}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    printStatement() {
      window.print();
    },
    resetFilters() {
      this.requestId += 1;
      this.listRequestIds.terms += 1;
      this.listRequestIds.classes += 1;
      this.listRequestIds.students += 1;
      this.listLoading.terms = false;
      this.listLoading.classes = false;
      this.listLoading.students = false;
      this.filters = {
        RegisteredTermId: "",
        ClassId: "",
        StudentId: "",
        Option: "",
        Section: "",
        StartDate: "",
        EndDate: "",
      };
      this.classes = [];
      this.students = [];
      this.clearReport();
      this.search = "";
      this.loadRegisteredTerms();
    },
    async toggleMode() {
      this.mockMode = !this.mockMode;
      this.listRequestIds.terms += 1;
      this.listRequestIds.classes += 1;
      this.listRequestIds.students += 1;
      this.listLoading.terms = false;
      this.listLoading.classes = false;
      this.listLoading.students = false;
      this.filters = {
        RegisteredTermId: "",
        ClassId: "",
        StudentId: "",
        Option: "",
        Section: "",
        StartDate: "",
        EndDate: "",
      };
      this.classes = [];
      this.students = [];
      this.clearReport();
      await this.loadRegisteredTerms();
    },
  },
};
</script>

<style scoped>
.statement-shell {
  padding-bottom: 3rem;
}
.page-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
}
.page-heading h1 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #162b4d;
  margin: 0.3rem 0;
}
.page-heading p,
.section-heading p {
  margin: 0;
  color: #718096;
}
.eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #3974a8;
}
.heading-tools {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}
.mode-pill {
  border-radius: 20px;
  padding: 0.45rem 0.75rem;
  background: #edf5f8;
  color: #256c75;
  font-size: 0.78rem;
  font-weight: 600;
}
.mode-pill i {
  margin-right: 0.4rem;
}
.mode-pill.sample {
  background: #fff4df;
  color: #8b5e12;
}
.filter-panel,
.chart-card,
.table-card {
  border-radius: 12px;
}
.section-heading {
  margin-bottom: 1.1rem;
}
.section-heading h2,
.section-heading h3,
.table-heading h3 {
  font-size: 1rem;
  font-weight: 700;
  color: #263b58;
  margin: 0 0 0.25rem;
}
.filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  gap: 0.2rem 1rem;
}
.filter-grid .form-group {
  margin-bottom: 0.75rem;
}
.filter-grid label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #43536a;
  display: flex;
  justify-content: space-between;
}
.inline-loader {
  font-size: 0.7rem;
  color: #54738f;
  font-weight: 500;
}
.inline-loader i {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 2px solid #cbd5e0;
  border-top-color: #3182ce;
  border-radius: 50%;
  vertical-align: -2px;
  margin-right: 4px;
  animation: spin 0.7s linear infinite;
}
.filter-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.2rem;
}
.filter-note {
  color: #718096;
  font-size: 0.78rem;
  margin: 0.35rem 0 0;
}
.sample-banner,
.notice {
  display: flex;
  gap: 0.7rem;
  align-items: flex-start;
  padding: 0.85rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  background: #fff7e8;
  color: #75520f;
  font-size: 0.85rem;
}
.notice {
  background: #f2f7fb;
  color: #52677d;
  margin: 1rem 0 1.2rem;
}
.statement-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  background: #fff;
  padding: 1.3rem 1.5rem;
  border-radius: 12px 12px 0 0;
  border-bottom: 1px solid #e8edf2;
}
.statement-header h2 {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0.2rem 0;
  color: #203650;
}
.statement-header p {
  margin: 0;
  color: #708198;
  font-size: 0.85rem;
}
.student-identity {
  text-align: right;
  display: grid;
  gap: 0.22rem;
  color: #53677e;
  font-size: 0.83rem;
}
.student-identity strong {
  font-size: 1rem;
  color: #203650;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  background: #fff;
  padding: 1rem 1.5rem 1.35rem;
  border-radius: 0 0 12px 12px;
}
.summary-card {
  border: 1px solid #e7edf3;
  border-radius: 10px;
  padding: 1rem;
  min-height: 112px;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.summary-card small {
  font-size: 0.75rem;
  color: #65768a;
  font-weight: 600;
}
.summary-card strong {
  font-size: 1.3rem;
  color: #294c6e;
  white-space: nowrap;
}
.summary-card span {
  font-size: 0.72rem;
  color: #8794a4;
}
.summary-card.positive strong,
.summary-card.settled strong {
  color: #247a59;
}
.summary-card.attention strong {
  color: #a8661c;
}
.summary-card .period-value {
  font-size: 1rem;
}
.chart-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.2rem;
}
.chart-card .card-body {
  padding: 1.25rem;
}
.chart-card h3 {
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 0.2rem;
  color: #263b58;
}
.chart-card p {
  margin: 0;
  color: #718096;
  font-size: 0.8rem;
}
.bar-chart {
  height: 205px;
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  padding: 1.25rem 0.5rem 0.1rem;
  overflow-x: auto;
}
.bar-column {
  height: 100%;
  min-width: 58px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 0.4rem;
}
.bar-column strong {
  font-size: 0.68rem;
  color: #53677e;
}
.bar-column i {
  display: block;
  width: min(44px, 70%);
  background: linear-gradient(180deg, #56a6ca, #337ca8);
  border-radius: 5px 5px 1px 1px;
  min-height: 3px;
}
.bar-column small {
  font-size: 0.68rem;
  color: #74859a;
}
.compare-chart {
  display: grid;
  gap: 1.2rem;
  margin-top: 1.25rem;
}
.compare-row {
  display: grid;
  grid-template-columns: 120px 1fr auto;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.76rem;
  color: #586b80;
}
.compare-row > div {
  height: 10px;
  background: #edf1f5;
  border-radius: 8px;
  overflow: hidden;
}
.compare-row i {
  display: block;
  height: 100%;
  border-radius: 8px;
}
.compare-row i.blue {
  background: #4b8eb8;
}
.compare-row i.green {
  background: #4c9b77;
}
.compare-row i.amber {
  background: #d59b4c;
}
.compare-row strong {
  font-size: 0.73rem;
  color: #374c62;
  white-space: nowrap;
}
.chart-empty {
  padding: 3rem 1rem;
  text-align: center;
  color: #8794a4;
  font-size: 0.85rem;
}
.table-card {
  background: #fff;
  overflow: hidden;
}
.table-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 1.2rem 1.3rem;
  border-bottom: 1px solid #e8edf2;
}
.table-heading p {
  margin: 0;
  color: #7b8999;
  font-size: 0.78rem;
}
.table-tools {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}
.search-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #dce3ea;
  border-radius: 6px;
  padding: 0.35rem 0.6rem;
  margin: 0;
  color: #8794a4;
}
.search-box input {
  border: 0;
  outline: 0;
  width: 150px;
  font-size: 0.8rem;
}
.statement-table {
  font-size: 0.8rem;
}
.statement-table thead th {
  background: #f7f9fb;
  color: #56677b;
  border: 0;
  white-space: nowrap;
  cursor: pointer;
}
.statement-table tbody td {
  vertical-align: middle;
  color: #4d5e72;
}
.statement-table .amount {
  font-weight: 600;
  color: #277454;
}
.statement-table tfoot th {
  background: #f7f9fb;
  border-top: 1px solid #e8edf2;
}
.empty-state,
.welcome-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #718096;
}
.empty-state > i,
.welcome-state > i {
  font-size: 1.6rem;
  color: #92a7b8;
}
.empty-state h3,
.welcome-state h2 {
  font-size: 1.05rem;
  color: #324b67;
  margin: 0.8rem 0 0.3rem;
}
.empty-state p,
.welcome-state p {
  margin: 0;
  font-size: 0.85rem;
}
.pagination-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1.2rem;
  border-top: 1px solid #edf0f3;
  color: #758397;
  font-size: 0.78rem;
}
.pagination-row > div {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}
.pagination-row select {
  border: 1px solid #dce3ea;
  border-radius: 5px;
  padding: 0.25rem;
  color: #56677b;
}
.statement-footer {
  padding: 1rem 0.2rem;
  color: #8491a0;
  font-size: 0.75rem;
}
.welcome-state {
  border-radius: 12px;
}
.welcome-state > i {
  font-size: 2rem;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 992px) {
  .filter-grid {
    grid-template-columns: repeat(2, minmax(145px, 1fr));
  }
  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 640px) {
  .filter-grid,
  .chart-grid {
    grid-template-columns: 1fr;
  }
  .summary-grid {
    grid-template-columns: 1fr 1fr;
    padding: 1rem;
  }
  .statement-header {
    flex-direction: column;
  }
  .student-identity {
    text-align: left;
  }
  .table-tools {
    width: 100%;
  }
  .search-box {
    flex: 1;
  }
  .page-heading h1 {
    font-size: 1.45rem;
  }
  .compare-row {
    grid-template-columns: 95px 1fr;
  }
  .compare-row strong {
    grid-column: 2;
  }
}
</style>

<style>
@media print {
  @page {
    size: auto;
    margin: 14mm;
  }
  body * {
    visibility: hidden !important;
  }
  #printable-statement,
  #printable-statement * {
    visibility: visible !important;
  }
  #printable-statement {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    padding: 0 !important;
  }
  #printable-statement .table-heading .table-tools,
  #printable-statement .pagination-row {
    display: none !important;
  }
  #printable-statement .statement-header,
  #printable-statement .summary-grid,
  #printable-statement .notice,
  #printable-statement .chart-grid,
  #printable-statement .table-card,
  #printable-statement .statement-footer {
    break-inside: avoid;
    box-shadow: none !important;
  }
  #printable-statement .statement-table thead {
    display: table-header-group;
  }
  #printable-statement .statement-table tr {
    break-inside: avoid;
  }
}
</style>
